'use client'

import { cn } from '@brew/ui/cn'
import { ArrowUpRight, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { IntegrationBrandLogo } from '@/app/(app)/integrations/components/integration-brand-logo'
import {
  MK_BODY,
  MK_CAPTION,
  MK_CARD_TITLE,
} from '@/components/marketing/home/home-layout'
import {
  TILE,
  TileCopy,
  TilePanel,
} from '@/components/marketing/home/tile-panel'
import { IntentPrefetchLink } from '@/components/navigation/intent-prefetch-link'
import {
  getIntegrationTypeLabel,
  type IntegrationMeta,
  integrationData,
} from '@/lib/integrations/catalog'

type Direction = IntegrationMeta['dataDirection']

const SECTIONS: ReadonlyArray<{
  id: Direction
  title: string
  description: string
}> = [
  {
    id: 'data_out',
    title: 'Email destinations',
    description: 'Send the emails you make in Brew to the ESP you already use.',
  },
  {
    id: 'data_in',
    title: 'Data sources',
    description: 'Pull design files, customer events, and feedback into Brew.',
  },
]

const DIRECTION_LABEL: Record<Direction, string> = {
  data_in: 'In',
  data_out: 'Out',
}

const LINK_TILE = cn(
  TILE,
  'transition-colors duration-150 hover:bg-mk-card focus-visible:outline-2 focus-visible:outline-mk-accent focus-visible:-outline-offset-2'
)

function TileBody({ integration }: { integration: IntegrationMeta }) {
  const isComingSoon = integration.availability === 'coming_soon'
  return (
    <>
      <span className="flex size-10 shrink-0 items-center justify-center overflow-clip rounded-[10px] bg-white shadow-[0_0_0_1px_var(--mk-ring)]">
        <IntegrationBrandLogo
          variant="card"
          brandDomain={integration.brandDomain}
          provider={integration.id}
          name={integration.name}
          tone={integration.logoTone}
          background={integration.logoBackground}
          className="size-full"
          imgClassName="size-full object-cover"
        />
      </span>
      <TileCopy title={integration.name} className="pr-6">
        {integration.shortDescription}
      </TileCopy>
      <span
        className={cn(
          MK_CAPTION,
          'mt-auto flex items-center justify-between gap-2 text-mk-body'
        )}
      >
        <span>
          {getIntegrationTypeLabel(integration.integrationType)} ·{' '}
          {DIRECTION_LABEL[integration.dataDirection]}
        </span>
        {isComingSoon ? (
          <span className="font-medium text-mk-accent">Coming soon</span>
        ) : null}
      </span>
      {isComingSoon ? null : (
        <ArrowUpRight
          aria-hidden="true"
          className="absolute top-5 right-5 size-4 text-mk-ink/75"
        />
      )}
    </>
  )
}

/** One integration: a link to its page, or a muted tile while it is coming soon. */
function IntegrationDirectoryTile({
  integration,
}: {
  integration: IntegrationMeta
}) {
  if (integration.availability === 'coming_soon') {
    return (
      <div className={cn(TILE, 'opacity-60')}>
        <TileBody integration={integration} />
      </div>
    )
  }
  return (
    <IntentPrefetchLink
      href={`/browse/integrations/${integration.id}`}
      className={LINK_TILE}
    >
      <TileBody integration={integration} />
    </IntentPrefetchLink>
  )
}

/**
 * The public integrations directory in the home page's language: a search
 * field, then the catalog split into destinations and sources, each a
 * `TilePanel` of linked tiles (logo, name, one line, how it connects).
 * Coming-soon integrations stay visible but never link.
 */
export function IntegrationDirectory() {
  const [query, setQuery] = useState('')

  const sections = useMemo(() => {
    const needle = query.toLowerCase().trim()
    const matches = needle
      ? integrationData.filter(
          (integration) =>
            integration.name.toLowerCase().includes(needle) ||
            integration.shortDescription.toLowerCase().includes(needle)
        )
      : integrationData
    return SECTIONS.map((section) => ({
      ...section,
      items: matches.filter(
        (integration) => integration.dataDirection === section.id
      ),
    })).filter((section) => section.items.length > 0)
  }, [query])

  return (
    <div className="flex w-full flex-col gap-12">
      <label className="relative mx-auto w-full max-w-xl">
        <span className="sr-only">Search integrations</span>
        <Search
          aria-hidden="true"
          className="absolute top-1/2 left-5 size-4 -translate-y-1/2 text-mk-body"
        />
        <input
          type="search"
          name="integration-search"
          autoComplete="off"
          spellCheck={false}
          placeholder="Search integrations"
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          className={cn(
            MK_BODY,
            'h-12 w-full rounded-full border border-mk-line bg-mk-tile pr-5 pl-11 text-mk-ink outline-none transition-colors placeholder:text-mk-body focus-visible:border-mk-ink/30'
          )}
        />
      </label>

      {sections.length === 0 ? (
        <p className={cn(MK_BODY, 'py-12 text-center text-mk-body')}>
          No integrations match &ldquo;{query}&rdquo;.
        </p>
      ) : (
        sections.map((section) => (
          <section
            key={section.id}
            aria-labelledby={`integrations-${section.id}`}
            className="flex flex-col gap-6"
          >
            <div className="flex flex-col gap-1.5">
              <h2
                id={`integrations-${section.id}`}
                className={cn(MK_CARD_TITLE, 'text-mk-ink')}
              >
                {section.title}
              </h2>
              <p className={cn(MK_BODY, 'text-mk-body')}>
                {section.description}
              </p>
            </div>
            <TilePanel columns={3}>
              {section.items.map((integration) => (
                <IntegrationDirectoryTile
                  key={integration.id}
                  integration={integration}
                />
              ))}
            </TilePanel>
          </section>
        ))
      )}
    </div>
  )
}
